// NOWC250 の配色定義 (2026-07-19)。
// タイルはパレット PNG (index=Lv値0-10) で、色はここで定義した PLTE/tRNS を
// タイルのバイト列へ差し替えて決める (生成側の色は「従来色」で焼かれているが、
// classic 以外を選んだ時のみ差し替えが走る)。凡例も同じ定義から描く。

export type PaletteId = "classic" | "alt";

// [R, G, B, A] × パレットindex 0..10
// index: 0=レーダー圏外 / 1=雨なし(透明) / 2..10=弱→強
export type PaletteColors = [number, number, number, number][];

export interface NowcPalette {
    id: PaletteId;
    label: string;
    colors: PaletteColors;
}

const CLASSIC_COLORS: PaletteColors = [
    [204, 204, 204, 128], // 0 レーダー圏外 (グレー半透明)
    [255, 255, 255, 0], //   1 雨なし (透明)
    [102, 255, 255, 255], // 2 うす水色
    [51, 204, 255, 255], //  3 水色
    [17, 102, 255, 255], //  4 青
    [17, 255, 51, 255], //   5 緑
    [255, 255, 51, 255], //  6 黄
    [255, 153, 17, 255], //  7 オレンジ
    [255, 17, 255, 255], //  8 マゼンタ
    [255, 17, 17, 255], //   9 赤
    [153, 17, 51, 255], //  10 赤黒
];

// 新色 (2026-07-20 発注者決定)。P60 の境界値でいうと
// 1未満/1〜/5〜 は従来色と同じ、10〜=FFFF33 20〜=FFCC33 30〜=FF9911(同)
// 50〜=FF1111 80〜=DD1155 150〜=991133(同)。
/*
const ALT_COLORS: PaletteColors = [
    [204, 204, 204, 128], // 0 レーダー圏外 (グレー半透明)
    [255, 255, 255, 0], //   1 雨なし (透明)
    [102, 255, 255, 255], // 2 #66FFFF (1未満)
    [51, 204, 255, 255], //  3 #33CCFF (1〜)
    [17, 102, 255, 255], //  4 #1166FF (5〜)
    [255, 255, 51, 255], //  5 #FFFF33 (10〜)
    [255, 204, 51, 255], //  6 #FFCC33 (20〜)
    [255, 153, 17, 255], //  7 #FF9911 (30〜)
    [255, 17, 17, 255], //   8 #FF1111 (50〜)
    [221, 17, 119, 255], //  9 #DD1177 (80〜)
    [153, 17, 51, 255], //  10 #991133 (150〜)
];
*/
const ALT_COLORS: PaletteColors = [
    [204, 204, 204, 128], // 0 レーダー圏外 (グレー半透明)
    [255, 255, 255, 0], //   1 雨なし (透明)
    [230, 230, 255, 255], // 2
    [160, 210, 255, 255], // 3
    [33, 140, 255, 255], //  4
    [0, 65, 255, 255], //    5
    [250, 245, 0, 255], //   6
    [255, 153, 0, 255], //   7
    [255, 40, 0, 255], //    8
    [204, 0, 104, 255], //   9
    [136, 17, 51, 255], //  10
];

export const PALETTES: Record<PaletteId, NowcPalette> = {
    classic: { id: "classic", label: "従来色", colors: CLASSIC_COLORS },
    alt: { id: "alt", label: "新色", colors: ALT_COLORS },
};

// デフォルトは新色 (2026-07-20 発注者指示)。従来色は sty_pal=classic で明示指定
export function paletteById(id: string | null | undefined): NowcPalette {
    return id === "classic" ? PALETTES.classic : PALETTES.alt;
}

// ---- PNG の PLTE/tRNS 差し替え ----

const CRC_TABLE = (() => {
    const table = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
        let c = n;
        for (let k = 0; k < 8; k++) {
            c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
        }
        table[n] = c >>> 0;
    }
    return table;
})();

function crc32(bytes: Uint8Array, start: number, length: number) {
    let c = 0xffffffff;
    for (let i = start; i < start + length; i++) {
        c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
    }
    return (c ^ 0xffffffff) >>> 0;
}

// パレット PNG の PLTE (RGB×11) と tRNS (A×11) をこの配色に書き換えた複製を返す。
// チャンク構造だけを歩くので画素データには触れない (~0.01ms/タイル)。
export function applyPaletteToPng(
    source: ArrayBuffer,
    palette: NowcPalette,
): ArrayBuffer {
    const bytes = new Uint8Array(source.slice(0));
    let offset = 8; // PNG シグネチャ
    while (offset + 8 <= bytes.length) {
        const length =
            (bytes[offset] << 24) |
            (bytes[offset + 1] << 16) |
            (bytes[offset + 2] << 8) |
            bytes[offset + 3];
        const type = String.fromCharCode(
            bytes[offset + 4],
            bytes[offset + 5],
            bytes[offset + 6],
            bytes[offset + 7],
        );
        const dataStart = offset + 8;
        if (type === "PLTE") {
            for (let i = 0; i < palette.colors.length; i++) {
                const [r, g, b] = palette.colors[i];
                if (dataStart + i * 3 + 2 < bytes.length) {
                    bytes[dataStart + i * 3] = r;
                    bytes[dataStart + i * 3 + 1] = g;
                    bytes[dataStart + i * 3 + 2] = b;
                }
            }
            writeCrc(bytes, offset, length);
        } else if (type === "tRNS") {
            for (let i = 0; i < palette.colors.length && i < length; i++) {
                bytes[dataStart + i] = palette.colors[i][3];
            }
            writeCrc(bytes, offset, length);
        } else if (type === "IDAT" || type === "IEND") {
            break; // パレット系チャンクは IDAT より前にある
        }
        offset = dataStart + length + 4;
    }
    return bytes.buffer;
}

function writeCrc(bytes: Uint8Array, chunkOffset: number, dataLength: number) {
    const crc = crc32(bytes, chunkOffset + 4, dataLength + 4);
    const at = chunkOffset + 8 + dataLength;
    bytes[at] = (crc >>> 24) & 0xff;
    bytes[at + 1] = (crc >>> 16) & 0xff;
    bytes[at + 2] = (crc >>> 8) & 0xff;
    bytes[at + 3] = crc & 0xff;
}

// 凡例・UI 用: 表示クラス (弱→強 = index 2..10) の CSS 色
export function legendCssColors(palette: NowcPalette): string[] {
    return palette.colors
        .slice(2)
        .map(([r, g, b]) => `rgb(${r},${g},${b})`);
}
